import emojiRegex from 'emoji-regex';
import { get, isArray, toString, isObject, trim, every, isEmpty, forEach, omit, replace, isNil, isNaN, isString } from 'lodash';
import { addValidator, format } from 'redux-form-validators';
import { formatNumber } from './format';

export function cleanInput(value, removeEmoji = true) {
    if (!value) {
        return "";
    }
    if (removeEmoji === true) {
        const emojis = emojiRegex();
        return value.toString().replace(emojis, "");
    }
    return value;
}

export function cleanFormValues(values, beforeSend = false) {
    // Manipulate values if beforeSend is not false
    const newValues = beforeSend !== false ? beforeSend(values) : values;

    // Clone object
    let data = Object.assign({}, newValues);

    // Convert all empty string to null
    Object.entries(data).map(([k, v]) => {
        if (trim(v) === "") {
            data[k] = null;
            return true;
        }
        return false;
    })

    if (data.location && isObject(data.location)) {
        // Convert child to string && update the "" from location object as API doesn't support it
        Object.entries(data.location).map(([k, v]) => {
            if (trim(v) === "") {
                data["location"][k] = null;
                return true;
            }
            data["location"][k] = toString(v);
            return false;
        })
    }

    return data;
}

export function normalizeErrors(errors) {
    let newErrors = {};

    Object.entries(errors).forEach(([key, value]) => {
        const fieldName = get(key.split("-"), 1, key);

        if (isArray(value)) {
            if (isObject(value[0])) {
                // convert array of objects into an object with numeric keys
                let obj = {};
                value.forEach((v, i) => {
                    obj[i] = normalizeErrors(v);
                });
                newErrors[fieldName] = obj;
            } else {
                // array of strings → unwrap if single, join into one string if multiple
                newErrors[fieldName] =
                    value.length === 1 ? value[0] : value.join(" ");
            }
        } else if (isObject(value)) {
            // recurse for nested objects
            newErrors[fieldName] = normalizeErrors(value);
        } else {
            // primitive (string/number/boolean)
            newErrors[fieldName] = value;
        }
    });

    return newErrors;
}

export function transformErrors(error) {
    let serverError = ["Error in contacting API. Please try in 10 minutes"];
    const errors = get(error, "response.data", false);
    if (isArray(errors)) {
        return errors
    }
    if (get(errors, 'message')) {
        return [get(errors, 'message')];
    }
    if (errors !== false) {
        serverError = normalizeErrors(errors);
    }
    if (errors === false && process.env.REACT_APP_MODE !== "prod" && error.message) {
        serverError = [error.message];
    }
    return serverError;
}

export function nl2br(str, is_xhtml) {
    if (typeof str === 'undefined' || str === null) {
        return '';
    }
    var breakTag = (is_xhtml || typeof is_xhtml === 'undefined') ? '<br />' : '<br>';
    return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag + '$2');
}

export const getPasswordValidationRegex = () => {
    return format({ with: /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*\W).{7,}\S+$/, message: 'Should contains: [A-Z] [a-z] [0-9] [#?!@$%^&*-] [No Space]' });
}

export const phoneNumberValidation = () => {
    return format({ with: /\s*(([+](\s?\d)([-\s]?\d)|0)?(\s?\d)([-\s]?\d){9}|[(](\s?\d)([-\s]?\d)+\s*[)]([-\s]?\d)+)\s*/, message: 'Invalid telephone no', allowBlank: true });
}

export const getStringValidationRegex = () => {
    return format({ with: /^[A-Za-z\s.`'-]+$/, message: 'Only characters are allowed', allowBlank: true });
}

export const getPasswordValidationInfo = () => {
    return "Create a strong password with a mix of characters. Password must be at least 8 characters, include A-Z, a-z, 0-9, special characters (#?!@$%^&*-), and contain no spaces";
}

export function getSelectedItem(state, props) {
    const { value, prompt = "", disabled = false } = state;
    const { list, all } = props;

    if (all !== false && value === "all") {
        return { "all": all };
    }

    if (disabled === true || value === "") {
        return { "": prompt };
    }

    let item = list.find(d => toString(d.id) === toString(value));
    return item ? item : { "": prompt }
}

export const getReactSelectOptions = (arr = []) => {
    const transformedArray = arr.map(item => ({
        id: item.id,
        text: item.text,
        value: item.id,
        label: item.text
    }));
    return transformedArray;
}

const isTrulyEmpty = obj => {
    const filteredObj = omit(obj, ['type']); // Exclude "type" property
    return isEmpty(filteredObj) || every(filteredObj, value => value === undefined || value === "" || value === null);
}

export const checkIfArrayOfObjectsIsEmpty = (array) => {
    return isEmpty(array) || array.every(isTrulyEmpty);
}

export const isAnyKeyValid = (obj) => {
    let isValid = false;
    forEach(obj, (value, key) => {
        if (!["type"].includes(key)) {
            if (value) {
                isValid = true;
            }
        }
    });
    return isValid;
};

export const isAdult = addValidator({
    validator: (_, value) => {
        if (!value) return true;

        const birthDate = new Date(value * 1000);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();

        const monthDiff = today.getMonth() - birthDate.getMonth();
        const dayDiff = today.getDate() - birthDate.getDate();

        if (age < 18 || (age === 18 && (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)))) {
            return false;
        }
        if (age > 100 || (age === 100 && (monthDiff > 0 || (monthDiff === 0 && dayDiff > 0)))) {
            return false;
        }
        return true;
    },
});

export const getDisplayValue = (value, numberFormat, focus, error, minDecimals = 2, maxDecimals = 5) => {
    if (numberFormat === true) {
        if (focus || (error && !focus)) {
            return replace(value, /,/g, '');
        }
        return formatNumber(value, "", "", minDecimals, maxDecimals);
    }
    return value;
};

export const isValid = (value) => {
    return !(
        isNil(value) ||                    // null or undefined
        isNaN(value) ||                    // NaN
        value === 0 ||                       // numeric zero
        value === false ||                   // boolean false (optional)
        (isString(value) && trim(value) === '') || // empty string
        (isArray(value) && isEmpty(value)) ||      // empty array
        (isObject(value) && isEmpty(value))        // empty object
    );
}

export const viewRenderers = (type) => {
    if (type === "date") {
        return {
            hours: null,
            minutes: null,
            seconds: null
        }
    }
    return {}
}

export const validateDate = addValidator({
    validator: (options, value) => {
        if (!value) return true;

        const {
            ">": after = false,
            "<": before = false,
            ">=": onOrAfter = false,
            "<=": onOrBefore = false,
        } = options;

        const valNum = Number(value);

        if (onOrAfter !== false) {
            const compare = Number(onOrAfter);
            if (valNum < compare) {
                return false;
            }
        }

        if (after !== false) {
            const compare = Number(after);
            if (valNum <= compare) {
                return false;
            }
        }

        if (onOrBefore !== false) {
            const compare = Number(onOrBefore);
            if (valNum > compare) {
                return false;
            }
        }

        if (before !== false) {
            const compare = Number(before);
            if (valNum >= compare) {
                return false;
            }
        }

        return true
    }
});

export const zeroOrMin500 = addValidator({
    validator: (_, value) => {
        const num = Number(value);
        if (!value) {
            return true;
        }
        if (num === 0) {
            return true;
        }
        if (num >= 500) {
            return true;
        }
        return false;
    }
});